import type { ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CAMPOS_CON_ASISTENCIA } from "@/lib/ia";
import { cn } from "@/lib/utils";

import { CampoConAsistencia } from "./CampoConAsistencia";
import { EncabezadoNumerado } from "./EncabezadoNumerado";

interface DescripcionDeLaMaquinariaProps {
  informeId: number;
  /** "01" sin lista (la casilla «no cuenta con maquinaria» la esconde) y "02" con ella. */
  numero: string;
  descripcion: string;
  onDescripcion: (texto: string) => void;
  validada: boolean;
  onValidada: (validada: boolean) => void;
  disabled: boolean;
  /** La fuente que declaró la IA para el texto, mientras siga siendo el propuesto. */
  fuente?: ReactNode;
  /** La caja plegable «Registro de terreno» (y «Redactar desde terreno») que va bajo el texto. */
  terreno?: ReactNode;
}

/**
 * La tarjeta de la descripción general de la maquinaria.
 *
 * LA CASILLA «Información validada» VA AL LADO DE «Mejorar redacción» y no
 * abajo: este texto lo puede haber propuesto la IA, y alguien tiene que
 * hacerse responsable de que dice lo que corresponde. Lo que el encargado
 * grabó para esta descripción se ve en la caja plegable de abajo (`terreno`),
 * con su «Redactar desde terreno»: el diseño la dibuja bajo el texto, no en un
 * panel lateral como en Construcciones.
 */
export function DescripcionDeLaMaquinaria({
  informeId,
  numero,
  descripcion,
  onDescripcion,
  validada,
  onValidada,
  disabled,
  fuente,
  terreno,
}: DescripcionDeLaMaquinariaProps) {
  const pildora = (
    <label
      className={cn(
        "flex w-fit cursor-pointer items-center gap-2 rounded-md border-[1.5px] px-2.5 py-1.5 text-[0.71875rem] font-bold",
        validada
          ? "bg-success-soft/50 border-success-dot/40 text-success-soft-foreground"
          : "bg-background text-ink-muted",
      )}
    >
      <Checkbox
        checked={validada}
        disabled={disabled}
        onCheckedChange={(v) => onValidada(v === true)}
      />
      Información validada
    </label>
  );

  return (
    <section
      id="maquinarias-descripcion"
      aria-labelledby="maquinarias-descripcion-titulo"
      className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
    >
      <EncabezadoNumerado
        numero={numero}
        idDelTitulo="maquinarias-descripcion-titulo"
        titulo="Descripción de la maquinaria y equipos"
        bajada="Texto general que se imprime en el informe: estado de conservación y relevancia para la explotación."
      />

      <div className="mt-3.5 flex min-w-0 flex-col gap-2">
        <Label htmlFor="descripcion-maquinaria" className="sr-only">
          Descripción de la maquinaria y equipos de la propiedad
        </Label>
        <CampoConAsistencia
          id="descripcion-maquinaria"
          campo={CAMPOS_CON_ASISTENCIA.descripcionMaquinaria}
          value={descripcion}
          onChange={onDescripcion}
          informeId={informeId}
          rows={5}
          variantDeMejora="outline"
          placeholder="Describe en general la maquinaria y equipos presentes en la propiedad, su estado de conservación y relevancia para la explotación…"
          disabled={disabled}
          accionesDespues={pildora}
        />
        {fuente}
        {terreno}
      </div>
    </section>
  );
}
